import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Badge from "../../components/basicComponent/Badge";
import MuiRadioButton from "../basicComponent/MuiRAdioButton";
import {
  calculateFare,
  parseCustomDateString,
  parseDynamicFareRules,
} from "../../constants/helper";
import { passenger } from "@/constants/types";
import { useCommission } from "../../services/customHooks/useCommission";
import { useGlobalState } from "../../services/context/AuthContext";

type FlightRoundTripCardProps = {
  journeyData?: any;
  flightData?: any;
  flightName: string;
  flightCode: string;
  flightTakeoffTime: string;
  origin: string;
  totaltime: string;
  stop: string | number;
  flightReachTime: string;
  destination: string;
  totalFare: number | string; // <- allow number
  flightImg: string;
  departureDate: string;
  departureTime: string;
  departureTerminal: string;
  checkInBaggageWeight: string;
  cabinBaggage: string;
  paxfare: any;
  inboundData?: any;
  outboundData?: any;
  flightId: string;
  onFlightSelect: (flightId: string, direction: "outbound" | "inbound") => void;
  selectedFlightId?: string;
  direction: "outbound" | "inbound";
  traceId?: string;
  travellerDetail: passenger;
  isIntl?: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

const FlightRoundTripCard = ({
  travellerDetail,
  flightData,
  destination,
  flightCode,
  flightReachTime,
  flightTakeoffTime,
  origin,
  stop,
  totalFare,
  totaltime,
  isExpanded,
  onToggleExpand,
  flightImg,
  paxfare,
  flightId,
  onFlightSelect,
  journeyData,
  selectedFlightId,
  direction,
  isIntl,
}: FlightRoundTripCardProps) => {
  const [value, setValue] = useState("1");
  const { commission } = useCommission(journeyData, travellerDetail);
  const { userInfo } = useGlobalState();
  const passengerCount =
    travellerDetail?.adultCount +
    travellerDetail?.childrenCount +
    travellerDetail?.infantCount;
  // ensure a valid tab is selected whenever we expand
  useEffect(() => {
    if (isExpanded && value === "") setValue("1");
  }, [isExpanded, value]);

  const handleChange = (_e: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const totalFlightFare = Number(totalFare) + commission;
  const fareData = calculateFare(paxfare);
  // Parse the rules dynamically (gives you cancelFee/changeFee/emtFee, etc.)
  const { fareDetails: dynamicFareRules, emtFee } = parseDynamicFareRules(
    flightData?.FareRule
  );

  const isSelected = selectedFlightId === flightId;

  const handleRadioChange = (id: string) => {
    onFlightSelect(id, direction);
  };

  const bondToUse = journeyData?.Bonds?.[0];

  const tdeatil = {
    ...travellerDetail,
    adultFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 0)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    childFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 1)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    infantFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 2)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    totalFare: flightData?.Fare?.TotalFareWithOutMarkUp ?? 0,
    totalTax: flightData?.Fare?.TotalTaxWithOutMarkUp ?? 0,
  };
  const currency = userInfo?.currencyCode || "AED";
  const isInvalidDateSelection = () => {
    if (!journeyData?.OutboundDate || !journeyData?.InboundDate) return false;

    const outboundDate = new Date(journeyData.OutboundDate);
    const inboundDate = new Date(journeyData.InboundDate);

    return inboundDate <= outboundDate; // overlap ya same
  };

  return (
    <div
      className={`card mt-3 cursor-pointer ${
        isSelected ? "selected-card" : ""
      }`}
      onClick={() => handleRadioChange(flightId)}
    >
      <div
        className="flex flex-wrap items-center"
        style={{ padding: "16px 16px 14px 13px" }}
      >
        <div
          className="flex flex-wrap items-center flightRoundTrip"
          style={{ gap: "20px 12px" }}
        >
          <div className="pad-left2">
            <div className="flex justify-center items-center size-14 shrink-0 grid grid-col gap-1 p-0 mx-2 flight-img">
              <img src={flightImg} alt="flightImg" />
              <span
                className="text-2sm text-gray-700 mb-3"
                style={{ width: "50px" }}
              >
                {flightCode}
              </span>
            </div>
          </div>

          <div className="col-xs-4 mag">
            <div className="grid grid-col gap-1">
              <div className="flight-time">{flightTakeoffTime}</div>
              <div className="rating">
                <div className="rating-label checked desti-text">
                  <p className="text-2sm text-gray-700">{origin}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden-sm hidden-xs mar-pad text-center">
            <div className="grid grid-col gap-1">
              <span className="text-2sm text-gray-700">{totaltime}</span>
              <div className="text-base font-medium text-gray-900 hover:text-primary-active mb-px">
                <img
                  src="https://flight.easemytrip.com/Content/img/arow_main.png"
                  alt="arow_main"
                />
              </div>
              <span className="text-2sm text-gray-700">
                {stop === 0 ? "Non-Stop" : `${stop}-stop`}
              </span>
            </div>
          </div>

          <div className="grid grid-col gap-1">
            <div className="flight-time">{flightReachTime}</div>
            <div className="rating">
              <div className="rating-label checked desti-text">
                <p className="text-2sm text-gray-700 ">{destination}</p>
              </div>
            </div>
          </div>

          <div className="c mr">
            <div className="grid gap-2">
              <div className="flight-rate ps-3">
                {currency} {totalFlightFare}
              </div>
            </div>
          </div>

          {/* <div className="ms-2">
            <MuiRadioButton
              name={`flight-selection-${direction}`}
              value={isSelected ? "selected" : ""}
              onChange={handleRadioChange}
              variant="withOutLabel"
            />
          </div> */}
        </div>
      </div>

      <div>
        <div className="flight-detail ps-3">
          <span
            onClick={() => {
              onToggleExpand();
              setValue("1");
            }}
          >
            Flight Details
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="accordion-content roundtrip-tab">
          <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                >
                  <Tab label="Flight Information" value="1" />
                  <Tab label="Fare Details" value="2" />
                  <Tab label="Baggage Information" value="3" />
                  <Tab label="Cancellation & Change Rule" value="4" />
                </TabList>
              </Box>

              <TabPanel value="1">
                {bondToUse?.Legs?.map((leg: any, legIndex: number) => (
                  <div
                    key={`${flightId}-${legIndex}`}
                    className="d-flex justify-content-between flex-wrap mb-4"
                  >
                    <div>
                      <img src={flightImg} alt="flightImg" />
                    </div>

                    <div>
                      <div className="desti-text">{leg.DepartureTime}</div>
                      <div className="desti-text">
                        <p>{leg.Origin}</p>
                      </div>
                      <span className="text-2sm text-gray-700">
                        {(() => {
                          const parsed = parseCustomDateString(
                            leg.DepartureDate
                          );
                          return parsed
                            ? `${parsed.day}, ${parsed.date} ${parsed.month} ${parsed.year}`
                            : "";
                        })()}
                      </span>
                      <div>
                        <span className="text-2sm text-gray-700">
                          Terminal - {leg.DepartureTerminal || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <span className="text-2sm text-gray-700 ps-4">
                        {leg.Duration}
                      </span>
                      <div>
                        <img
                          src="https://flight.easemytrip.com/Content/img/arow_main.png"
                          alt="Flight Route"
                        />
                      </div>
                      <span className="text-2sm text-gray-700 ps-4">
                        <i className="fa-solid fa-suitcase-rolling"></i>{" "}
                        {leg.BaggageWeight || "0kg"} | Cabin:{" "}
                        {leg.CabinBagWT || "0kg"}
                      </span>
                    </div>

                    <div>
                      <div className="text-base font-medium text-gray-900 hover:text-primary-active mb-px">
                        {leg.ArrivalTime}
                      </div>
                      <div className="desti-text">
                        <p>{leg.Destination}</p>
                      </div>
                      <span className="text-2sm text-gray-700">
                        {(() => {
                          const parsed = parseCustomDateString(leg.ArrivalDate);
                          return parsed
                            ? `${parsed.day}, ${parsed.date} ${parsed.month} ${parsed.year}`
                            : "";
                        })()}
                      </span>
                      <div>
                        <span className="text-2sm text-gray-700">
                          Terminal - {leg.ArrivalTerminal || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </TabPanel>

              <TabPanel value="2">
                <div className="da_adultsection mb-3">
                  <div className="float-left w-100 brdr-radius-5 mg-btm-20">
                    <div className="fare-card">
                      <div className="fare-item">
                        <span>{tdeatil?.adultCount} x Adult</span>
                        <span>
                          {currency} {tdeatil?.adultFare * tdeatil?.adultCount}
                        </span>
                      </div>
                      <div className="fare-item">
                        <span>{tdeatil?.childrenCount} x Child</span>
                        <span>
                          {currency}{" "}
                          {tdeatil?.childFare * tdeatil?.childrenCount}
                        </span>
                      </div>
                      <div className="fare-item">
                        <span>{tdeatil?.infantCount} x Infant</span>
                        <span>
                          {currency}{" "}
                          {tdeatil?.infantFare * tdeatil?.infantCount}
                        </span>
                      </div>

                      <div className="fare-item">
                        <span>Total Tax</span>
                        <span>
                          {currency} {tdeatil.totalTax}
                        </span>
                      </div>
                      <div className="fare-item">
                        <span>other</span>
                        <span>
                          {currency} {commission}
                        </span>
                      </div>
                      <div className="fare-item total">
                        <span>Total (Fee & Surcharge)</span>
                        <span>
                          {currency} {totalFlightFare}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel value="3">
                {/* your baggage table stays as-is */}

                <div className="card accordion col-lg-12" id="accordionExample">
                  <div id="collapseTwo">
                    <div className="baggagerule mgt15 mar-to-12">
                      <div style={{ overflowX: "auto", width: "100%" }}>
                        <table width="100%" rules="all" border={1}>
                          <tbody>
                            <tr>
                              <th>
                                <p className="frtbl_hd">Airline</p>
                              </th>
                              <th>
                                <p className="frtbl_hd">Check-in Baggage</p>
                              </th>
                              <th>
                                <p className="frtbl_hd">Cabin Baggage</p>
                              </th>
                            </tr>

                            {bondToUse?.Legs?.map((leg: any, index: number) => (
                              <>
                                <tr key={`baggage-${index}`}>
                                  <td>
                                    <div
                                      className="flgi-l"
                                      style={{
                                        float: "left",
                                        width: 32,
                                        marginRight: 10,
                                      }}
                                    >
                                      <img src={flightImg} alt="flightImg" />
                                    </div>
                                    <div
                                      style={{ float: "left", fontSize: 11 }}
                                    >
                                      <p>
                                        {leg.AirlineName || "Unknown Airline"}
                                      </p>
                                      <p>{leg.FlightNumber}</p>
                                    </div>
                                  </td>
                                  <td>{leg.BaggageWeight || "N/A"}Kg</td>
                                  <td>{leg.CabinBagWT || "N/A"}Kg</td>
                                </tr>
                              </>
                            ))}

                            <tr>
                              <td colSpan={3}>
                                <div className="termsbagg">
                                  <ul>
                                    <li>
                                      Baggage information mentioned above is
                                      obtained from airline's reservation
                                      system, EWTS does not guarantee the
                                      accuracy of this information.
                                    </li>
                                    <li>
                                      The baggage allowance may vary according
                                      to stop-overs, connecting flights, or
                                      changes in airline rules.
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel value="4">
                <div className="card card-grid h-full min-w-full">
                  <div className="card-header">
                    <h3 className="card-title">Fare Rules</h3>
                    <div className="refund-text">
                      <Badge
                        borderColor="#2dca1c"
                        text={
                          flightData?.IsRefundable
                            ? "Refundable"
                            : "Non-Refundable"
                        }
                      />
                    </div>
                  </div>

                  <div className="card-body">
                    {/* Cancellation Table */}
                    <div className="datatable-initialized">
                      <div className="scrollable-x-auto">
                        <table className="table table-border pt-2">
                          <thead>
                            <tr>
                              <th>Timeframe to Cancel</th>
                              <th>Airline Fees</th>
                              <th>EWTS Fees</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dynamicFareRules.map(
                              ({ start, end, cancelFee }, index) => (
                                <>
                                  {console.log(
                                    "dynamicFareRules",
                                    start,
                                    end,
                                    cancelFee
                                  )}

                                  <tr key={`cancel-${start}-${end}`}>
                                    <td>
                                      Cancel before {end} hours of departure
                                    </td>
                                    <td>
                                      <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                        {currency} {cancelFee ?? 0}
                                      </a>
                                    </td>
                                    <td>
                                      <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                        {currency} {emtFee ?? 0}
                                      </a>
                                    </td>
                                  </tr>
                                </>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Rescheduling Table */}
                    <div className="datatable-initialized mt-4">
                      <div className="scrollable-x-auto">
                        <table className="table table-border pt-2">
                          <thead>
                            <tr>
                              <th style={{ width: "562px" }}>
                                Timeframe to Reschedule
                              </th>
                              <th>Airline Fees</th>
                              <th>EWTS Fees</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dynamicFareRules.map(
                              ({ start, end, changeFee }, index) => (
                                <tr key={`change-${start}-${end}`}>
                                  <td>
                                    Reschedule before {end} hours of departure
                                  </td>
                                  <td>
                                    <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                      {currency} {changeFee ?? 0}
                                    </a>
                                  </td>
                                  <td>
                                    <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                      {currency} {emtFee ?? 0}
                                    </a>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="row">
                      <div className="col-md-12 col-sm-12 col-xs-12 terms-h ms-4 mb-2">
                        Terms &amp; Conditions
                      </div>
                      <div className="col-md-12 col-sm-12 col-xs-12">
                        <div className="terms mar10">
                          <ul>
                            <li>
                              Total Rescheduling Charges = Airline Fees + Fare
                              Difference (if applicable) + EWTS Fees.
                            </li>
                            <li>
                              The airline cancel/reschedule fees are indicative
                              and may change without prior notice.
                            </li>
                            <li>
                              EWTS does not guarantee the accuracy of
                              cancel/reschedule fees.
                            </li>
                            <li>
                              Partial cancellation is not allowed on special
                              discounted round-trip fares.
                            </li>
                            <li>
                              No additional baggage allowance for infants.
                            </li>
                            <li>
                              Some restricted fares may not allow amendments or
                              cancellation.
                            </li>
                            <li>
                              Airline penalties should be reconfirmed before
                              requesting cancellation or amendment.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabContext>
          </Box>
        </div>
      )}
    </div>
  );
};

export default FlightRoundTripCard;
